import { getRuntimeConfig } from '@/lib/config/runtime';

export type ApiDiagnostics = {
  environment: {
    isClient: boolean;
    isServer: boolean;
    inIframe: boolean;
    iframeOrigin: string | null;
    currentOrigin: string;
    isCrossOrigin: boolean;
  };
  configuration: {
    apiBaseUrl: string | undefined;
    apiMode: string | undefined;
    forceMockInIframe: boolean;
  };
  recommendations: string[];
};

/**
 * Genera un reporte de diagnóstico del estado de la API
 */
export const getApiDiagnostics = (): ApiDiagnostics => {
  const isClient = typeof window !== 'undefined';
  const config = getRuntimeConfig();

  const diagnostics: ApiDiagnostics = {
    environment: {
      isClient,
      isServer: !isClient,
      inIframe: false,
      iframeOrigin: null,
      currentOrigin: isClient ? window.location.origin : 'N/A (server)',
      isCrossOrigin: false,
    },
    configuration: {
      apiBaseUrl: config.VITE_API_BASE_URL,
      apiMode: config.VITE_API_MODE,
      forceMockInIframe: config.VITE_FORCE_MOCK_IN_IFRAME === 'true',
    },
    recommendations: [],
  };

  if (!isClient) {
    return diagnostics;
  }

  // Detectar iframe
  try {
    diagnostics.environment.inIframe = window.self !== window.top;

    if (diagnostics.environment.inIframe) {
      try {
        diagnostics.environment.iframeOrigin = window.top?.location.origin || null;
        diagnostics.environment.isCrossOrigin = false;
      } catch {
        diagnostics.environment.isCrossOrigin = true;
        diagnostics.environment.iframeOrigin = document.referrer
          ? new URL(document.referrer).origin
          : 'unknown (cross-origin)';
      }
    }
  } catch {
    diagnostics.environment.inIframe = true;
    diagnostics.environment.isCrossOrigin = true;
  }

  // Generar recomendaciones
  if (diagnostics.environment.isCrossOrigin) {
    diagnostics.recommendations.push(
      'Detected cross-origin iframe environment. CORS restrictions may apply.',
    );
    diagnostics.recommendations.push(
      'Consider setting VITE_API_MODE=mock or VITE_FORCE_MOCK_IN_IFRAME=true',
    );
  }

  if (
    !diagnostics.configuration.apiBaseUrl ||
    diagnostics.configuration.apiBaseUrl === 'undefined'
  ) {
    diagnostics.recommendations.push(
      'VITE_API_BASE_URL is not configured. Set it to your API URL or http://localhost:3001 for development.',
    );
  }

  if (diagnostics.environment.inIframe && !diagnostics.configuration.forceMockInIframe) {
    diagnostics.recommendations.push(
      'Running in iframe. If you experience fetch errors, set VITE_FORCE_MOCK_IN_IFRAME=true',
    );
  }

  return diagnostics;
};

/**
 * Imprime el diagnóstico en consola de forma legible
 */
export const printApiDiagnostics = (): void => {
  const diagnostics = getApiDiagnostics();

  /* eslint-disable no-console */
  console.group('🔍 API Diagnostics');

  console.group('Environment');
  console.table(diagnostics.environment);
  console.groupEnd();

  console.group('Configuration');
  console.table(diagnostics.configuration);
  console.groupEnd();

  if (diagnostics.recommendations.length > 0) {
    console.group('⚠️ Recommendations');
    diagnostics.recommendations.forEach((rec, i) => {
      console.log(`${i + 1}. ${rec}`);
    });
    console.groupEnd();
  } else {
    console.log('✅ No issues detected');
  }

  console.groupEnd();
  /* eslint-enable no-console */
};

// Exponer en window para debugging en consola
if (typeof window !== 'undefined') {
  (window as unknown as { __apiDiagnostics: unknown }).__apiDiagnostics = {
    get: getApiDiagnostics,
    print: printApiDiagnostics,
  };
}
